import { Outlet, useSearchParams } from 'react-router-dom';
import TabNavigation from './TabNavigation';
import LobbyBackBar from './LobbyBackBar';
import { isFromLobby } from '@/utils/lobbyNavigation';
import {
  APP_COLUMN_BG,
  APP_GUTTER_BG,
  DESKTOP_COLUMN,
  DESKTOP_COLUMN_FRAME,
} from '@/utils/desktopColumn';

/**
 * Main Layout with Tab Navigation and Content Area
 * This wraps all main views (Play, My Games, Recent)
 * App entry (/) shows RulesView or redirects - no MainLayout/tabs.
 * Desktop optimization: centred column from `md` up — width lives in DESKTOP_COLUMN
 * Tab Navigation is the topmost in-flow element for iframe/game embedding.
 * LobbyBackBar floats above when the user arrived from the external Lobby.
 */
export default function MainLayout() {
  const [searchParams] = useSearchParams();

  /**
   * Arriving from the Lobby means the player came in on one specific contest, and the Lobby's
   * own My Games tabs (Upcoming / Live / Recent) are what they navigate contests with. Showing
   * Squares' Play / My Games / Recent tabs on top of that is a second, competing set of the
   * same idea, so from the Lobby this renders the game alone — LobbyBackBar is the only
   * navigation, and it goes back where the player came from.
   *
   * Standalone Squares (no `from=lobby`) is unaffected and keeps its tabs.
   */
  const showTabs = !isFromLobby(searchParams);

  return (
    <div className={`min-h-screen md:h-screen md:min-h-0 ${APP_GUTTER_BG} flex flex-col`}>
      {/*
       * Desktop container wrapper — single source of the column width.
       *
       * `flex-1 flex flex-col` is load-bearing: the page is a flex column, so this
       * stretches the column (and its frame) to full viewport height instead of
       * stopping at content height, and it is what makes <main>'s own `flex-1`
       * mean anything.
       *
       * `md:min-h-0` is what actually bounds <main>. A flex item's automatic minimum
       * size is its min-content height, so without this the column floors at the
       * height of everything inside it and quietly grows past the `md:h-screen` root —
       * <main> then sizes to its content and its `overflow-y-auto` never engages. (An
       * earlier pass assumed the scroll container was already live because <main> is a
       * scroller and so contributes 0 itself; measuring says otherwise — the floor is
       * imposed here, on this box, not on <main>.) With it, the column is exactly one
       * viewport from `md` up and <main> becomes a REAL inner scroll container.
       *
       * That is scoped to `md` deliberately: mobile keeps document scrolling, so the
       * DK iframe's scroll chaining and address-bar behaviour on phones are untouched.
       * On desktop, scrolling moves from the document into <main>: load-more still
       * works (`usePaginatedScroll` walks up to the nearest scrollable parent and finds
       * <main>), but tab-switch scroll position now persists.
       */}
      <div
        className={`w-full flex-1 md:min-h-0 flex flex-col ${APP_COLUMN_BG} ${DESKTOP_COLUMN} ${DESKTOP_COLUMN_FRAME}`}
      >
        {/* Floating back-to-lobby control — only when arriving from the Lobby */}
        <LobbyBackBar />

        {/* Tab Navigation - topmost in-flow element. Hidden when embedded from the Lobby. */}
        {showTabs && <TabNavigation />}

        {/*
         * Main Content Area
         *
         * `md:h-screen` on the page root above is what makes <main> a *bounded* box
         * from `md` up rather than one that grows with its content. With that bound,
         * `overflow-y-auto` is a real inner scroller: Play, My Games load-more, and
         * tab-switch scroll position all live here. Below `md` nothing changes — the
         * page is still `min-h-screen` and the document scrolls as before.
         */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
